import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useJsonLd, getSiteUrl } from "@/hooks/useJsonLd";

export default function ArticlePoczatkujacy() {
  const navigate = useNavigate();
  const siteUrl = getSiteUrl();
  useEffect(() => { window.scrollTo(0, 0); document.title = "Poradnik Początkującego Kakobuy – Od A do Z | Kakobuy Spreadsheet"; }, []);

  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Poradnik Początkującego Kakobuy – Od A do Z w 15 Minut",
    "description": "Szybki start z Kakobuy Spreadsheet — w 15 minut nauczysz się zamawiać produkty z Chin, nawet jeśli robisz to pierwszy raz.",
    "url": `${siteUrl}/artykul/poradnik-poczatkujacego-kakobuy`,
    "datePublished": "2026-06-21",
    "dateModified": "2026-06-21",
    "inLanguage": "pl",
    "author": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Polska",
      "url": siteUrl,
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Polska",
      "url": siteUrl,
    },
    "image": "https://readdy.ai/api/search-image?query=Open%20notebook%20with%20handwritten%20notes%20and%20fashion%20items%20on%20light%20oatmeal%20surface%2C%20soft%20warm%20lighting%2C%20editorial%20lifestyle%20photography%2C%20learning%20beginners%20concept%2C%20minimal%20composition%2C%20cozy%20atmosphere&width=1600&height=900&seq=kako-art-09-hero&orientation=landscape",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/artykul/poradnik-poczatkujacego-kakobuy`,
    },
  });

  return (
    <>
      <Navbar />
      <main>
        <section className="relative min-h-[55vh] flex flex-col overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url(https://readdy.ai/api/search-image?query=Open%20notebook%20with%20handwritten%20notes%20and%20fashion%20items%20on%20light%20oatmeal%20surface%2C%20soft%20warm%20lighting%2C%20editorial%20lifestyle%20photography%2C%20learning%20beginners%20concept%2C%20minimal%20composition%2C%20cozy%20atmosphere&width=1600&height=900&seq=kako-art-09-hero&orientation=landscape)" }}></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/35"></div>
          <div className="relative z-10 flex-1 flex items-center pt-24 pb-12">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <span className="inline-block px-4 py-1.5 bg-background-50/60 backdrop-blur-md rounded-full text-xs font-semibold text-foreground-800 mb-6 whitespace-nowrap">Blog &gt; Poradnik</span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight">Poradnik<br />Początkującego<br />Kakobuy</h1>
              <p className="text-base text-background-50/85 mt-4 max-w-2xl">Szybki start z Kakobuy Spreadsheet — w 15 minut nauczysz się zamawiać produkty z Chin, nawet jeśli robisz to pierwszy raz.</p>
            </div>
          </div>
        </section>

        <article className="py-16 md:py-24 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="prose prose-lg max-w-none">
              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">Od A do Z z <span className="italic text-foreground-600 font-normal">Kakobuy Spreadsheet</span></h2>
                <p className="text-foreground-700 leading-relaxed mb-4">Zakupy z Chin przez <strong>Kakobuy Spreadsheet</strong> są prostsze niż myślisz. Wystarczy 15 minut, aby opanować podstawy i złożyć pierwsze zamówienie. Poniżej znajdziesz kompletny przewodnik.</p>

                <div className="space-y-4 mb-6">
                  {[{ title: "1. Znajdź produkt w arkuszu", text: "Otwórz arkusz na kaspreadsheet.com i wybierz kategorię. Przeglądaj produkty i kliknij link, który Cię interesuje." }, { title: "2. Sprawdź szczegóły", text: "Na stronie produktu sprawdź zdjęcia, opis, tabelę rozmiarów i opinie innych kupujących. Upewnij się, że sprzedawca ma dobre oceny." }, { title: "3. Dodaj do koszyka", text: "Wybierz rozmiar, kolor i ilość, a następnie dodaj produkt do koszyka w Kakobuy." }, { title: "4. Wybierz wysyłkę", text: "Wybierz metodę dostawy do Polski — standardową (10-20 dni), ekspresową (5-10 dni) lub priorytetową (3-7 dni)." }, { title: "5. Zapłać i czekaj", text: "Dokonaj płatności przez PayPal lub kartę. Twoje zamówienie zostanie zrealizowane, a Ty otrzymasz numer do śledzenia przesyłki." }].map((step, i) => (
                    <div key={i} className="flex items-start gap-4 p-5 bg-background-100 rounded-xl">
                      <div className="w-9 h-9 rounded-full bg-accent-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-bold">{i + 1}</div>
                      <div><h4 className="font-semibold text-foreground-900 mb-1">{step.title}</h4><p className="text-sm text-foreground-600">{step.text}</p></div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">FAQ dla początkujących</h2>
                <div className="space-y-4">
                  <div className="bg-background-100 rounded-xl p-5"><h4 className="font-semibold text-foreground-900 mb-1">Czy potrzebuję konta Kakobuy?</h4><p className="text-sm text-foreground-600">Tak, musisz założyć darmowe konto, aby składać zamówienia. Rejestracja zajmuje 2 minuty.</p></div>
                  <div className="bg-background-100 rounded-xl p-5"><h4 className="font-semibold text-foreground-900 mb-1">Czy mogę zamówić jedną sztukę?</h4><p className="text-sm text-foreground-600">Tak, nie ma minimalnej ilości zamówienia. Możesz kupić pojedynczy produkt.</p></div>
                  <div className="bg-background-100 rounded-xl p-5"><h4 className="font-semibold text-foreground-900 mb-1">Co jeśli produkt nie dotrze?</h4><p className="text-sm text-foreground-600">Kakobuy oferuje ochronę kupującego. Jeśli produkt nie dotrze, otrzymasz pełny zwrot.</p></div>
                </div>
              </section>

              <section className="text-center py-10">
                <p className="text-lg text-foreground-700 mb-6">Gotowy na pierwsze zakupy? <strong>Kakobuy Spreadsheet</strong> czeka.</p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="bg-foreground-900 text-background-50 px-7 py-3 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer">Rozpocznij zakupy</a>
                  <a href="/" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer" onClick={(e) => { e.preventDefault(); navigate("/"); }}><i className="ri-arrow-left-line mr-1"></i>Strona główna</a>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}